import { formatInTimeZone } from 'date-fns-tz';
import { API_ENDPOINTS, DEFAULT_APPOINTMENT_SLUG } from '../../../config/api';
import { formatCalendarDate } from '../../../utils/timezone';
import type { AppointmentTaker } from '../../marketplace/types';
import { FULL_NAME_OVERRIDES } from '../utils/consultantDisplay';

export const KEVIN_BUSINESS_HOURS = {
  start: 11,
  startMinute: 0,
  end: 18,
  endMinute: 0,
};

export const INDIA_BUSINESS_HOURS = {
  start: 10,
  startMinute: 0,
  end: 18,
  endMinute: 0,
};

export const KEVIN_BOOKING_EMAIL = 'kevin@canadawaleyar.com';

const INDIA_CONSULTANT_SLUGS = new Set(['rudra', 'prashant']);

const DELHI_OFFICE_ADDRESS: AppointmentTaker['officeAddress'] = {
  name: 'CWY Delhi Office',
  fullAddress:
    '8, near District Court Karkardooma, Rishabh Vihar, Karkardooma, Anand Vihar, New Delhi, Delhi, 110092',
  addressLine1: '8, near District Court Karkardooma, Rishabh Vihar',
  city: 'New Delhi',
  province: 'Delhi',
  postalCode: '110092',
};

const SLUG_TIMEZONE_OVERRIDES: Record<string, AppointmentTaker['timeZone']> = {
  prashant: 'Asia/Kolkata',
  rudra: 'Asia/Kolkata',
  dikshit: 'America/Toronto',
  ujjval: 'America/Toronto',
};

/** Per-slug currency overrides (wins over stale DB values). */
const SLUG_CURRENCY_OVERRIDES: Record<string, AppointmentTaker['currency']> = {
  prashant: 'INR',
  rudra: 'INR',
};

/** Per-slug office overrides for India-based consultants. */
const SLUG_OFFICE_OVERRIDES: Record<string, AppointmentTaker['officeAddress']> = {
  prashant: DELHI_OFFICE_ADDRESS,
  rudra: DELHI_OFFICE_ADDRESS,
};

export function normalizeTakerForBooking(taker: AppointmentTaker, slug: string): AppointmentTaker {
  const slugLower = slug.toLowerCase();
  const tzOverride = SLUG_TIMEZONE_OVERRIDES[slugLower];
  const currencyOverride = SLUG_CURRENCY_OVERRIDES[slugLower];
  const officeOverride = SLUG_OFFICE_OVERRIDES[slugLower];
  const freeConsultation = {
    fee: 0,
    currency: currencyOverride || taker.currency || 'CAD',
  };

  if (slugLower === DEFAULT_APPOINTMENT_SLUG) {
    return {
      ...taker,
      ...freeConsultation,
      displayName: 'Kevin',
      email: KEVIN_BOOKING_EMAIL,
      allowSaturday: true,
      allowSunday: false,
      allowWalkIn: taker.allowWalkIn !== false,
      allowCall: true,
      businessHours: KEVIN_BUSINESS_HOURS,
      timeZone: taker.timeZone || 'America/Toronto',
    };
  }

  // Everyone else defaults to America/Toronto if no DB value and no override
  const finalTz = tzOverride || taker.timeZone || 'America/Toronto';
  const fullNameOverride = FULL_NAME_OVERRIDES[slugLower];

  if (INDIA_CONSULTANT_SLUGS.has(slugLower)) {
    return {
      ...taker,
      ...freeConsultation,
      timeZone: finalTz,
      displayName: fullNameOverride || taker.displayName,
      officeAddress: officeOverride ?? taker.officeAddress,
      allowSaturday: true,
      allowSunday: false,
      allowWalkIn: taker.allowWalkIn !== false,
      allowCall: taker.allowCall !== false,
      businessHours: INDIA_BUSINESS_HOURS,
    };
  }

  return {
    ...taker,
    ...freeConsultation,
    timeZone: finalTz,
    displayName: fullNameOverride || taker.displayName,
    officeAddress: officeOverride ?? taker.officeAddress,
  };
}

export interface TimeSlot {
  start: Date;
  end: Date;
  available: boolean;
}

export interface BookingPayload {
  name: string;
  email: string;
  phone: string;
  countryCode: string;
  notes: string;
  /** Client booking message — stored on Appointment.booker_msg */
  booker_msg?: string;
  date: string;
  slotStart: string;
  slotEnd: string;
  appointmentType: 'walk-in' | 'call';
  serviceId: string;
  appointmentForUserId: string;
  appointmentForEmail: string;
  bookingReference: string;
  country?: string;
  timeZone?: string;
  couponCode?: string;
}

/** Maps app booking payload to POST /api/appointment/create body. */
export function toCreateBookingApiBody(payload: BookingPayload) {
  const slotStart = payload.slotStart || payload.date;
  const tz = payload.timeZone || 'America/Toronto';
  const bookerMsg = String(payload.booker_msg ?? payload.notes ?? '').trim() || undefined;
  return {
    serviceId: payload.serviceId,
    bookerName: payload.name,
    bookerEmail: payload.email,
    bookerPhone: payload.phone,
    phoneCountryCode: payload.countryCode,
    booker_msg: bookerMsg,
    // Keep notes empty for public bookings — staff notes are admin-only.
    notes: undefined,
    preferredDate: slotStart,
    preferredTimeSlot: formatInTimeZone(slotStart, tz, 'HH:mm'),
    appointmentType: payload.appointmentType,
    appointmentForUserId: payload.appointmentForUserId,
    appointmentForEmail: payload.appointmentForEmail,
    bookingReference: payload.bookingReference,
    couponCode: payload.couponCode,
  };
}

export interface CouponValidateResult {
  valid: boolean;
  discount?: number;
  isFree?: boolean;
  message?: string;
}

export interface StripeCheckoutResult {
  sessionUrl: string;
  sessionId?: string;
}

export interface StripeStatusResult {
  status: 'succeeded' | 'pending' | 'failed' | 'confirmed' | 'verified';
  transaction?: { status: string };
}

export interface CreateBookingResult {
  success: boolean;
  appointmentId?: string;
  message?: string;
}

export async function fetchTakers(): Promise<AppointmentTaker[]> {
  const res = await fetch(API_ENDPOINTS.APPOINTMENT_TAKERS);
  if (!res.ok) throw new Error('Failed to load consultants');
  const data = await res.json();
  const takers: AppointmentTaker[] = data.appointmentTakers ?? [];
  return takers
    .filter((t) => t.slug && t.slug.trim() !== '')
    .map((t) => normalizeTakerForBooking(t, t.slug!));
}

export async function fetchTakerBySlug(slug: string): Promise<AppointmentTaker> {
  const res = await fetch(API_ENDPOINTS.APPOINTMENT_TAKER_BY_SLUG(slug));
  if (!res.ok) throw new Error('Consultant not found');
  const data = await res.json();
  if (!data.success || !data.appointmentTaker) throw new Error('Consultant not found');
  return normalizeTakerForBooking(data.appointmentTaker, slug);
}

export async function fetchSlots(params: {
  date: Date;
  serviceId: string;
  appointmentForUserId?: string;
  appointmentForEmail?: string;
}): Promise<TimeSlot[]> {
  const url = new URL(API_ENDPOINTS.SLOTS);
  url.searchParams.set('date', formatCalendarDate(params.date));
  url.searchParams.set('serviceId', params.serviceId);
  if (params.appointmentForUserId) url.searchParams.set('appointmentForUserId', params.appointmentForUserId);
  else if (params.appointmentForEmail) url.searchParams.set('appointmentForEmail', params.appointmentForEmail);

  const res = await fetch(url.toString());
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch slots');
  }
  const data = await res.json();
  if (!data.success || !Array.isArray(data.availableSlots)) throw new Error('Failed to fetch slots');

  return data.availableSlots
    .map((slot: Record<string, unknown>) => {
      const rawStart = (slot.start ?? slot.startTime) as string | undefined;
      const rawEnd = (slot.end ?? slot.endTime) as string | undefined;
      if (!rawStart || !rawEnd) return null;
      const start = new Date(rawStart);
      const end = new Date(rawEnd);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
      return { start, end, available: Boolean(slot.available ?? slot.isAvailable ?? true) };
    })
    .filter((s: TimeSlot | null): s is TimeSlot => s !== null);
}

export async function fetchAvailability(params: {
  startDate: string;
  endDate: string;
  appointmentForEmail: string;
}) {
  const url = new URL(API_ENDPOINTS.AVAILABILITY);
  url.searchParams.set('startDate', params.startDate);
  url.searchParams.set('endDate', params.endDate);
  url.searchParams.set('appointmentForEmail', params.appointmentForEmail);
  const res = await fetch(url.toString());
  if (!res.ok) return { availability: [] };
  const data = await res.json();
  return { availability: data.availability ?? [] };
}

export async function validateCoupon(body: {
  code: string;
  email: string;
  amount: number;
  currency: string;
}): Promise<CouponValidateResult> {
  const res = await fetch(API_ENDPOINTS.COUPON_VALIDATE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  return {
    valid: data.valid ?? false,
    discount: data.discount,
    isFree: data.isFree,
    message: data.message,
  };
}

export async function applyCoupon(body: {
  code: string;
  email: string;
  bookingReference: string;
}): Promise<{ success: boolean }> {
  const res = await fetch(API_ENDPOINTS.COUPON_APPLY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  return { success: data.success ?? false };
}

export async function createStripeCheckout(body: {
  name: string;
  phone: string;
  email: string;
  country: string;
  amount: number;
  currency: string;
  message: string;
  successUrl: string;
  cancelUrl: string;
  bookingReference: string;
}): Promise<StripeCheckoutResult> {
  const res = await fetch(API_ENDPOINTS.STRIPE_CHECKOUT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok || !data.sessionUrl) throw new Error(data.error || 'Unable to start payment');
  return { sessionUrl: data.sessionUrl, sessionId: data.sessionId };
}

export async function pollStripeStatus(
  sessionId: string,
  maxRetries = 12,
  intervalMs = 3000
): Promise<StripeStatusResult> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    try {
      const res = await fetch(API_ENDPOINTS.STRIPE_STATUS(sessionId));
      const data = await res.json();
      const status = data.transaction?.status ?? data.status;
      if (['succeeded', 'confirmed', 'verified'].includes(status)) {
        return { status: 'succeeded', transaction: data.transaction };
      }
      if (status === 'failed') return { status: 'failed' };
    } catch {
      // retry on network errors
    }
  }
  throw new Error('Payment confirmation timed out. Please contact support if you were charged.');
}

export async function createBooking(payload: BookingPayload): Promise<CreateBookingResult> {
  const res = await fetch(API_ENDPOINTS.CREATE_BOOKING, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(toCreateBookingApiBody(payload)),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || 'Failed to create booking');
  return {
    success: true,
    appointmentId: data.appointmentId ?? data.bookingId,
    message: data.message,
  };
}
